import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import RibbonAppList from '../UI/RibbonAppList'
import {
  CONTENT_COLUMN_MAX_WIDTH,
  contentColumnMaxWidthSx,
  contentColumnPageShellSx
} from '../UI/contentColumn'
import ScanQrButton from '../UI/ScanQrButton'
import LegalFooterLinks from '../UI/LegalFooterLinks'
import anearApi from '../Api/AnearApi'
import UIStateContainer from '../Containers/UIStateContainer'
import * as logger    from 'loglevel'

const extractAppSlug = (app) => {
  const attrs = app?.app?.attributes || {}
  return attrs.slug || attrs['slug'] || app?.slug || app?.app?.slug || null
}

const useGlobalApps = () => {
  const [apps, setApps] = useState([])
  const [loading, setLoading] = useState(true)

  const uiContainer = UIStateContainer.useContainer()
  const { startProgress, progressComplete } = uiContainer

  useEffect(() => {
    let cancelled = false

    const appCreatedAtMs = playableApp => {
      const attrs = playableApp?.app?.attributes || {}
      const createdAt = attrs['created-at'] ?? attrs.created_at
      if (!createdAt) return 0
      const ms = new Date(createdAt).getTime()
      return Number.isFinite(ms) ? ms : 0
    }

    const fetchApps = async () => {
      try {
        startProgress()
        // Note: globalPlayableApps() returns ALL apps from the API (no server-side filtering)
        // We filter here in the UI to hide embeddable-only apps from user selection.
        // This allows embeddable apps to still be found via getAppBySlug() for child event creation.
        const arr = await anearApi.globalPlayableApps()
        if (!cancelled) {
          // Filter out apps that are not user-selectable (embeddable-only child apps).
          // user_selectable defaults to true, so only exclude if explicitly false
          // This is UI-only filtering - the API still returns all apps for programmatic access
          const selectableApps = arr.filter(app => {
            // JSON:API converts snake_case to kebab-case, so check both
            const attrs = app.app?.attributes || {}
            const userSelectable = attrs['user-selectable'] ?? attrs.user_selectable

            // If attribute is not present at all, default to true (backward compatibility)
            // Only exclude if explicitly set to false
            if (userSelectable === undefined || userSelectable === null) {
              return true // Default to visible
            }

            return userSelectable !== false
          })
          const sortedApps = selectableApps
            .slice()
            .sort((a, b) => appCreatedAtMs(b) - appCreatedAtMs(a))

          setApps(sortedApps)
          logger.info("globalPlayableApps loaded.")
        }
      } catch (err) {
        console.error(err)
      } finally {
        if (!cancelled) setLoading(false)
        progressComplete()
      }
    }

    fetchApps()
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return { apps, loading }
}

const appAttrs = app => {
  return {
    zoneId:       app.zone.id,
    appId:        app.app.id,
    slug:         extractAppSlug(app),
    shortName:    app.app.attributes['short-name'],
    longName:     app.app.attributes['long-name'],
    latestAppVersion: app.app.attributes['latest-app-version'],
    iconUrl:      app.app.attributes['icon-url'],
    description:  app.app.attributes['description'],
    requiresGeoLocation: app.eventRequiresGeoLocation()
  }
}

const AppsPage = () => {
  const { apps, loading } = useGlobalApps()
  if (loading) return null

  return (
    <>
      <Box sx={contentColumnPageShellSx}>
        <Box sx={{ ...contentColumnMaxWidthSx, overflowX: 'hidden' }}>
          <RibbonAppList
            apps={apps}
            buildState={appAttrs}
            footer={<LegalFooterLinks sx={{ pb: 0.5 }} />}
          />
        </Box>
      </Box>
      <Box
        sx={theme => ({
          position: 'fixed',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: theme.zIndex.tooltip + 1,
          //
          // Default: small phones → pin to the right gutter.
          //
          right: '1.25rem',
          left: 'auto',
          //
          // From sm and up, anchor relative to the centered 600px content column
          // so the QR button tucks just inside the right edge with padding.
          [theme.breakpoints.up('sm')]: {
            right: `calc((100vw - ${CONTENT_COLUMN_MAX_WIDTH.sm}px) / 2 + 1.5rem)`,
            left: 'auto'
          },
          [theme.breakpoints.up('md')]: {
            right: `calc((100vw - ${CONTENT_COLUMN_MAX_WIDTH.md}px) / 2 + 1.5rem)`,
            left: 'auto'
          }
        })}
      >
        <ScanQrButton />
      </Box>
    </>
  )
}

export default AppsPage
